// src/pages/DoctorsOverview/components/DoctorModal.js
import React, { useEffect } from "react";
import { Modal, Form, Input, Select, message, Button } from "antd";

import { useDispatch, useSelector } from "react-redux";
import { selectSelectedDoctor, selectIsEditDoctor } from "../DoctorsSelectors";
import {
  createOrUpdateDoctor,
  fetchDoctorsList,
  fetchClinicsList,
} from "../DoctorsThunk";
import { closeDoctorModal } from "../DoctorsSlice";

const DoctorModal = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const selected = useSelector(selectSelectedDoctor);
  const isEdit = useSelector(selectIsEditDoctor);
  const clinics = useSelector((state) => state.doctors?.clinicsList || []);

  useEffect(() => {
    dispatch(fetchClinicsList());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && selected) {
      form.setFieldsValue({
        doctor_name: selected.doctor || selected.doctor_name || "",
        emirates_id: selected.emirates_id || "",
        clinic_id: selected.clinic_id || selected.clinic_id || "",
        email: selected.email_address || "",
        status: selected.status || "",
      });
    } else {
      form.resetFields();
    }
  }, [selected, isEdit, form]);

  const handleCancel = () => {
    dispatch(closeDoctorModal());
    form.resetFields();
  };

  const handleFinish = async (values) => {
    // payload shape aligned with your old API (action + id)
    const payload = {
      doctor_name: values.doctor_name,
      email: values.email,
      dr_status: values.status || "active",
      clinic_name_id: values.clinic_id,
      action: isEdit ? "update" : "create",
      doctor_name_id: isEdit ? selected.doctor_id : undefined,
    };

    try {
      await dispatch(createOrUpdateDoctor(payload)).unwrap();
      message.success("Doctor saved successfully");
      dispatch(closeDoctorModal());
      dispatch(fetchDoctorsList());
    } catch (err) {
      message.error(err?.message || "Save failed");
    }
  };

  return (
    <Modal
      title={isEdit ? "Update Doctor" : "New Doctor"}
      visible={true}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          name="doctor_name"
          label="Doctor Name"
          data-testid="doctor_name"
          rules={[{ required: true, message: "Doctor Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email Address"
          data-testid="doctor_email_id"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="clinic_id"
          label="Clinic"
          data-testid="doctor_clinic"
          rules={[{ required: true, message: "Clinic is required" }]}
        >
          <Select
            showSearch
            options={(clinics || []).map((c) => ({
              label: c.clinic_name,
              value: c.id,
            }))}
            optionFilterProp="label"
            placeholder="Select clinic"
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: false }]}
          initialValue="active"
        >
          <Select
            disabled
            options={[
              { label: "active", value: "active" },
              { label: "inactive", value: "inactive" },
            ]}
          />
        </Form.Item>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 16,
          }}
        >
          <Button onClick={handleCancel}>Discard</Button>
          <Button type="primary" htmlType="submit"  data-testid="doctor_save">
            {isEdit ? "Update" : "Add Doctor"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default DoctorModal;
